import { useCallback, useEffect, useRef, useState } from 'react';
import type { Capture } from '@snapshots/domain';
import { useFrame } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import type { Refusal } from '@snapshots/transport';
import { Outage, Problems } from '@snapshots/ui';

/**
 * One snapshot as its page reads it (SNP-02): the snapshot last answered and when (`at`), a read the service
 * refused (`refused`, an answer) and a read nobody answered (`missed`, the page's one line, D163), kept apart
 * from a change's own answer, which the page holds. It is read when the page opens, after each renewal of the
 * session and when asked `again()`; what the service answers about it, a read, a change or the pending
 * card's reading, is `keep()`. Reading again never replaces a title typed and not saved.
 *
 * Every change raises a snapshot's `revision`, so an answer of an earlier revision than the one shown is an
 * older state, never kept: after a renewal the held change is sent again while the page reads the snapshot
 * again, and a read the service made before the change may answer after it.
 */
export class Snapshot {
	static use(id: string) {
		const { captures } = usePresence();
		const { renewed } = useFrame();
		const [capture, setCapture] = useState<Capture>();
		const [at, setAt] = useState<Date>();
		const [refused, setRefused] = useState<Refusal>();
		const [missed, setMissed] = useState<Refusal>();
		const [attempt, setAttempt] = useState(0);
		const [title, setTitle] = useState('');
		// The title last answered, so an answer follows the field only while it still shows that title
		const last = useRef<string>();
		// The revision shown, so an older answer arriving later never replaces it
		const revision = useRef(0);

		const keep = useCallback((found: Capture) => {
			if (found.revision < revision.current) return;
			revision.current = found.revision;
			setCapture(found);
			setAt(new Date());
			setRefused(undefined);
			setMissed(undefined);
			const before = last.current;
			last.current = found.title ?? '';
			setTitle(current => (before === undefined || current === before ? (found.title ?? '') : current));
		}, []);

		// One read of the snapshot; `live` says whether it is still wanted. Resolves whether it was answered
		const load = useCallback(
			async (live: () => boolean = () => true): Promise<boolean> => {
				try {
					const found = await captures.get(id);
					if (live()) keep(found);
					return true;
				} catch (cause) {
					const refusal = Problems.refusal(cause);
					// Not read while the session was ended: what is shown stays, and it is read again once renewed
					if (!live() || refusal.quiet) return true;
					if (Outage.cause(refusal)) {
						setMissed(refusal);
						return false;
					}
					setMissed(undefined);
					setRefused(refusal);
					return true;
				}
			},
			[captures, id, keep]
		);
		useEffect(() => {
			let live = true;
			void load(() => live);
			return () => {
				live = false;
			};
		}, [load, attempt, renewed]);

		return {
			capture,
			at,
			refused,
			missed,
			/** The title field: what the person typed, or the title last answered */
			title,
			/** What the person types in the title field */
			type: setTitle,
			keep,
			/** The snapshot read again, as the page's line and its Try again read it */
			load: () => load(),
			/** Reads it again on its own: after a change whose outcome is unknown, or one that answered nothing */
			again: () => setAttempt(value => value + 1),
			/** A reading of the pending card nobody answered: the page's line says it */
			miss: (refusal: Refusal) => setMissed(refusal)
		};
	}
}
